/**
 * chat-markdown-editor.js
 *
 * Utilities for the WYSIWYG contenteditable composer:
 *
 *   markdownToHtml(md)      — render markdown string → editor innerHTML (uses marked.js)
 *   htmlToMarkdown(el)      — serialize editor DOM → markdown string (custom serializer)
 *   applyInlineFormat(cmd)  — apply bold/italic/underline/strike via execCommand
 *   applyWrapFormat(o, c)   — apply spoiler / custom wrap (no execCommand equivalent)
 *   insertLink()            — insert markdown link at selection
 *   insertMentionChip(person, editorEl) — insert a non-editable @mention chip
 *   insertText(text)        — insert plain text at cursor
 *   getCaretContext()       — { text, offset } for @mention detection
 *   clearEditor(editorEl)   — reset to empty state
 *   focusEnd(editorEl)      — move caret to end
 */

import { marked }   from 'marked'
import TurndownService from 'turndown'

// Configure marked for inline-only, safe chat use
marked.use({
  breaks: true,
  gfm:    true,
})

// ── Turndown (HTML → Markdown) ────────────────────────────────────────────

const td = new TurndownService({
  emDelimiter:     '_',
  strongDelimiter: '**',
  hr:              '---',
})

// Mention chips → @[Name](id)
td.addRule('mention', {
  filter: node =>
    node.nodeName === 'SPAN' &&
    node.classList.contains('chat-editor-mention') &&
    node.dataset.mentionId,
  replacement: (_, node) => {
    const name = node.textContent.replace(/^@/, '')
    return `@[${name}](${node.dataset.mentionId})`
  },
})

// Spoiler spans → ||text||
td.addRule('spoiler', {
  filter: node =>
    node.nodeName === 'SPAN' &&
    node.classList.contains('chat-editor-spoiler'),
  replacement: content => `||${content}||`,
})

// Strikethrough → ~~text~~  (turndown uses ~text~ by default)
td.addRule('strikethrough', {
  filter: ['del', 's', 'strike'],
  replacement: content => `~~${content}~~`,
})

// Zero-width spaces (cursor anchors after mention chips) → strip
td.escape = str => str.replace(/\u200B/g, '')

// Placeholder used to protect @mention tokens from marked parsing
const MENTION_RE   = /@\[([^\]]+)\]\((\d+)\)/g
const PLACEHOLDER  = '\x02MENTION_'   // unlikely to appear in normal text
const PLACEHOLDER_RE = /\x02MENTION_(\d+)\x02/g

// ── Markdown → HTML (for loading/initialising the editor) ──────────────────

/**
 * Convert a markdown string into editor-safe HTML using marked.parseInline().
 * @mention tokens are extracted before parsing and re-inserted as chips.
 */
export function markdownToHtml(md) {
  if (!md) return ''

  // 1. Extract @mention tokens and replace with placeholders
  const mentions = []
  const protected_ = md.replace(MENTION_RE, (_, name, id) => {
    mentions.push({ name, id })
    return `${PLACEHOLDER}${mentions.length - 1}\x02`
  })

  // 2. Let marked handle standard inline markdown
  //    parseInline returns inline HTML without wrapping <p> tags
  let html = marked.parseInline(protected_)

  // 3. Re-insert mention chips (placeholders are now inside text nodes in html)
  html = html.replace(PLACEHOLDER_RE, (_, idx) => {
    const { name, id } = mentions[Number(idx)]
    const safeName = escapeHtml(name)
    return `<span class="chat-editor-mention" data-mention-id="${escapeHtml(id)}" contenteditable="false">@${safeName}</span>\u200B`
  })

  // 4. Restore <u> tags (marked escapes angle brackets — undo for our custom tag)
  html = html.replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gs, '<u>$1</u>')

  // 5. Spoiler: marked doesn't know ||…||, handle it ourselves on the raw string
  //    (already inside html at this point — apply as post-pass regex)
  html = html.replace(/\|\|(.+?)\|\|/gs, '<span class="chat-editor-spoiler">$1</span>')

  return html
}

// ── HTML → Markdown (serialize editor DOM back to markdown) ────────────────

/**
 * Serialize the editor element's current HTML to a markdown string.
 * Uses turndown with custom rules for mention chips and spoiler spans.
 *
 * Browsers with white-space:pre-wrap often insert literal \n in text nodes on
 * Shift+Enter; Turndown collapses those to spaces. Normalize to <br> first.
 */
export function htmlToMarkdown(editorEl) {
  if (!editorEl) return ''
  normalizeTextNewlines(editorEl)
  return td.turndown(editorEl).trim()
}

/** Replace literal newlines in text nodes with <br> so Turndown keeps line breaks. */
function normalizeTextNewlines(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes = []
  let node
  while ((node = walker.nextNode())) nodes.push(node)
  for (const textNode of nodes) {
    if (!textNode.nodeValue || !textNode.nodeValue.includes('\n')) continue
    const parts = textNode.nodeValue.split('\n')
    const frag = document.createDocumentFragment()
    parts.forEach((part, i) => {
      if (part) frag.appendChild(document.createTextNode(part))
      if (i < parts.length - 1) frag.appendChild(document.createElement('br'))
    })
    textNode.parentNode.replaceChild(frag, textNode)
  }
}

// ── Inline formatting ──────────────────────────────────────────────────────

/**
 * Apply standard inline formatting via execCommand.
 * Works reliably for bold, italic, underline, strikethrough.
 */
export function applyInlineFormat(command) {
  // eslint-disable-next-line no-undef
  document.execCommand(command, false, null)
}

/**
 * Wrap current selection (or insert markers) with open/close strings.
 * Used for spoiler (||…||) since there's no execCommand equivalent.
 */
export function applyWrapFormat(open, close) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return

  const range = sel.getRangeAt(0)
  const selected = range.toString()

  if (selected) {
    // Wrap selection
    const frag = document.createDocumentFragment()
    const openNode  = document.createTextNode(open)
    const closeNode = document.createTextNode(close)
    frag.appendChild(openNode)
    range.deleteContents()
    // Re-insert selected text between markers
    const contents = range.extractContents()
    frag.appendChild(contents)
    frag.appendChild(closeNode)
    range.insertNode(frag)
    // Move cursor after close marker
    range.setStartAfter(closeNode)
    range.collapse(true)
  } else {
    // Insert markers and place cursor between them
    const openNode  = document.createTextNode(open)
    const closeNode = document.createTextNode(close)
    range.insertNode(closeNode)
    range.insertNode(openNode)
    range.setStartAfter(openNode)
    range.setEndBefore(closeNode)
    range.collapse(true)
  }

  sel.removeAllRanges()
  sel.addRange(range)
}

/**
 * Insert a markdown link at the current selection.
 * If text is selected, uses it as the link label.
 */
export function insertLink() {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return

  const range    = sel.getRangeAt(0)
  const selected = range.toString()
  const md       = selected ? `[${selected}](url)` : `[](url)`

  range.deleteContents()
  const node = document.createTextNode(md)
  range.insertNode(node)

  // Select "url" placeholder so user can immediately type the URL
  const urlStart = (selected ? selected.length : 0) + 3
  const newRange = document.createRange()
  newRange.setStart(node, urlStart)
  newRange.setEnd(node, urlStart + 3)
  sel.removeAllRanges()
  sel.addRange(newRange)
}

// ── Mention chip ───────────────────────────────────────────────────────────

/**
 * Replace the current @query token with a non-editable mention chip.
 * Deletes backwards until (and including) the `@` that triggered the query.
 */
export function insertMentionChip(person, editorEl) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return

  const range  = sel.getRangeAt(0)
  const anchor = range.startContainer
  const offset = range.startOffset

  // Walk backwards from cursor to find the @ that triggered this mention
  let textNode = anchor
  let atOffset = -1

  if (textNode.nodeType === Node.TEXT_NODE) {
    const text = textNode.textContent.slice(0, offset)
    const idx  = text.lastIndexOf('@')
    if (idx !== -1) atOffset = idx
  }

  // Delete the @query text
  if (atOffset !== -1) {
    const deleteRange = document.createRange()
    deleteRange.setStart(textNode, atOffset)
    deleteRange.setEnd(textNode, offset)
    deleteRange.deleteContents()
    // Move caret to deletion point
    range.setStart(textNode, atOffset)
    range.collapse(true)
  }

  // Create the mention chip
  const chip = document.createElement('span')
  chip.className        = 'chat-editor-mention'
  chip.dataset.mentionId = String(person.id)
  chip.contentEditable  = 'false'
  chip.textContent      = `@${person.name}`

  // Insert chip + zero-width space (cursor anchor after chip)
  const zws = document.createTextNode('\u200B')
  range.insertNode(zws)
  range.insertNode(chip)

  // Move cursor to after the zero-width space
  const afterRange = document.createRange()
  afterRange.setStartAfter(zws)
  afterRange.collapse(true)
  sel.removeAllRanges()
  sel.addRange(afterRange)

  editorEl?.focus()
}

// ── Emoji / plain text insertion ───────────────────────────────────────────

/**
 * Insert a string at the current caret position.
 */
export function insertText(text) {
  // eslint-disable-next-line no-undef
  document.execCommand('insertText', false, text)
}

// ── Caret context (for @mention detection) ────────────────────────────────

/**
 * Returns the text before the caret within the current text node,
 * and the caret's absolute offset in that node.
 * Used to detect @query triggers.
 */
export function getCaretContext() {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return { text: '', offset: 0 }

  const range  = sel.getRangeAt(0)
  const anchor = range.startContainer
  if (anchor.nodeType !== Node.TEXT_NODE) return { text: '', offset: 0 }

  return {
    text:   anchor.textContent.slice(0, range.startOffset),
    offset: range.startOffset,
  }
}

// ── Editor lifecycle helpers ───────────────────────────────────────────────

export function clearEditor(editorEl) {
  if (!editorEl) return
  editorEl.innerHTML = ''
}

export function focusEnd(editorEl) {
  if (!editorEl) return
  editorEl.focus()
  const range = document.createRange()
  range.selectNodeContents(editorEl)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}

/**
 * Render message body markdown for chat bubbles (mentions + GFM + autolink).
 * Matches Rails chat_markdown / mobile EnrichedMarkdownText behaviour.
 */
export function renderMessageBody(md) {
  if (!md) return ''

  const mentions = []
  const protected_ = md.replace(MENTION_RE, (_, name, id) => {
    mentions.push({ name, id })
    return `${PLACEHOLDER}${mentions.length - 1}\x02`
  })

  let html = marked.parseInline(protected_)

  html = html.replace(PLACEHOLDER_RE, (_, idx) => {
    const { name } = mentions[Number(idx)]
    return `<span class="chat-mention">@${escapeHtml(name)}</span>`
  })

  html = html.replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gs, '<u>$1</u>')
  html = html.replace(/\|\|(.+?)\|\|/gs, '<span class="chat-spoiler">$1</span>')

  // Harden anchors: new tab, no opener, stay out of Turbo
  html = html.replace(/<a\s+([^>]*?)href="([^"]*)"([^>]*)>/gi, (_m, pre, href, post) => {
    const attrs = `${pre}${post}`
      .replace(/\s*target="[^"]*"/gi, '')
      .replace(/\s*rel="[^"]*"/gi, '')
      .replace(/\s*data-turbo="[^"]*"/gi, '')
    return `<a ${attrs} href="${href}" target="_blank" rel="noopener noreferrer" data-turbo="false">`
  })

  return html
}

/**
 * Render a markdown string into safe inline HTML for reply quotes and
 * the ReplyBar preview.  Uses marked.parseInline() so no <p> wrapper is added.
 * @mention tokens (@[Name](id)) are rendered as <span class="chat-preview-mention">.
 * Links are stripped (only the label is kept) to keep the preview single-line safe.
 */
export function renderMarkdownPreview(md) {
  if (!md) return ''

  // Convert @[Name](id) to @Name spans, strip other markdown links [text](url) → text
  let text = md
    .replace(/@\[([^\]]+)\]\(\d+\)/g, (_, name) =>
      `<span class="chat-preview-mention">@${escapeHtml(name)}</span>`)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')   // [text](url) → text

  let html = marked.parseInline(text)

  // Restore <u> tags (marked escapes angle brackets)
  html = html.replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gs, '<u>$1</u>')

  // Collapse spoiler (||text||) — show spoiler text in preview without the bars
  html = html.replace(/\|\|(.+?)\|\|/g, '$1')

  return html
}

// ── Internal helpers ───────────────────────────────────────────────────────

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
